import React, { useState, useEffect, forwardRef } from "react";
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

// Utility to calculate shift duration in hours
const calculateDurationHours = (startISO, endISO) => {
    if (!startISO || !endISO) return 0;
    const start = new Date(startISO);
    const end = new Date(endISO);
    const durationMs = end.getTime() - start.getTime();
    return durationMs / (1000 * 60 * 60); // Convert milliseconds to hours
};

// Utility to format hours into Xh Ym
const formatHoursMinutes = (hoursFloat) => {
    if (hoursFloat === 0) return '0h 0m';
    const totalMinutes = Math.round(hoursFloat * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
};


// --- Data Processing Utilities ---
const processReportData = (serverData) => {
    // serverData must be the object: { usersWithAccess: [], userTimelogs: [] }
    const { usersWithAccess, userTimelogs } = serverData;

    if (!usersWithAccess || !userTimelogs || userTimelogs.length === 0) {
        return [];
    }

    // 1. Create a map of users for fast lookup and initialization
    const employeeMap = usersWithAccess.reduce((acc, user) => {
        acc[user.userID] = {
            name: user.name,
            totalDurationHours: 0,
            punches: [],
        };
        return acc;
    }, {});

    // 2. Aggregate time logs and calculate duration
    userTimelogs.forEach(shift => {
        const user = employeeMap[shift.userID];

        if (!user) return;

        const endTime = shift.endTime ? new Date(shift.endTime) : null;
        const startTime = new Date(shift.startTime);
        let durationFormatted = 'N/A';
        let durationHours = 0;

        if (endTime) {
            durationHours = calculateDurationHours(shift.startTime, shift.endTime);
            durationFormatted = formatHoursMinutes(durationHours);
        } else {
            // Calculate duration up to current time if still clocked in
            const now = new Date();
            durationHours = calculateDurationHours(shift.startTime, now.toISOString());
            durationFormatted = `**Clocked In** (${formatHoursMinutes(durationHours)} so far)`;
        }

        // Update employee's total hours and add the detailed punch record
        user.totalDurationHours += durationHours;
        user.punches.push({
            date: startTime.toLocaleDateString(),
            clockIn: startTime.toLocaleTimeString(),
            clockOut: endTime ? endTime.toLocaleTimeString() : '**Clocked In**',
            duration: durationFormatted,
        });
    });

    // 3. Convert map back to an array for the state
    const usersData = Object.values(employeeMap).map(user => ({
        ...user,
        totalDurationHours: user.totalDurationHours,
        totalDurationFormatted: formatHoursMinutes(user.totalDurationHours)
    }));

    return usersData;
};

const processChartData = (usersData) => {
    return usersData.map(user => ({
        name: user.name,
        'Total Hours': parseFloat(user.totalDurationHours.toFixed(2))
    }));
};

export const ClockInReportPage = forwardRef(({ rawData }, ref) => {
    const [reportData, setReportData] = useState([]);
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        let sourceObject = null; // This will hold { usersWithAccess, userTimelogs }

        // 1. Check prop data first
        if (rawData && rawData.userTimelogs && rawData.userTimelogs.length > 0) {
            sourceObject = rawData;
        }

        // 2. If prop data is incomplete, try loading from sessionStorage
        if (!sourceObject) {
            const stored = sessionStorage.getItem("clockInReportData");
        
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    // FIX: Check if the required nested object exists under the 'rawData' key
                    if (parsed.rawData && parsed.rawData.userTimelogs) {
                        sourceObject = parsed.rawData;
                    }
                } catch (e) {
                    console.error("Error parsing sessionStorage data:", e);
                }
            }
        }

        // 3. Final processing check
        const isValidData = sourceObject &&
            sourceObject.userTimelogs &&
            sourceObject.userTimelogs.length > 0 &&
            sourceObject.usersWithAccess &&
            sourceObject.usersWithAccess.length > 0;

        if (isValidData) {
            const processedUsersData = processReportData(sourceObject);
            const processedChartData = processChartData(processedUsersData);
            setReportData(processedUsersData);
            setChartData(processedChartData);
        } else {
            setReportData([]);
            setChartData([]);
        }
    }, [rawData]);

    if (reportData.length === 0) {
        return (
            <Paper elevation={3} sx={{ p: 3, my: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    No data available for this report.
                </Typography>
            </Paper>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }} ref={ref}>
            {/* Visual Summary (Graph) */}
            <div style={{ pageBreakAfter: 'always' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 4 }}>

                    {/* Section 1: Visual Summary (Graph) - Using Recharts */}
                    <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '450px', backgroundColor: '#f5faff' }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Visual Summary</Typography>
                        <Box sx={{ minHeight: '300px', height: '100%', maxHeight: '400px' }}>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={chartData}
                                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                                    <Tooltip formatter={(value) => `${value.toFixed(2)} hours`} />
                                    <Legend />
                                    <Bar dataKey="Total Hours" fill="#1976d2" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>

                    {/* Employee Summary (Table) */}
                    <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '450px', backgroundColor: '#e1f5fe' }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Employee Summary</Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Total Hours</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((user, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                '&:nth-of-type(odd)': { backgroundColor: '#f8f8f8' },
                                                '&:hover': { backgroundColor: '#e0e0e0', cursor: 'pointer' }
                                            }}
                                        >
                                            <TableCell>{user.name}</TableCell>
                                            <TableCell>{user.totalDurationFormatted}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            </div>

            {/* Section 3: Detailed Clock-in Logs (Fully Open) */}
            <Box sx={{ mt: 2 }}>
                <Paper elevation={3} sx={{ p: 3, backgroundColor: '#f3e5f5' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Detailed Clock-in Logs</Typography>
                    {reportData.map((user, userIndex) => (
                        <div key={userIndex} style={{ pageBreakInside: 'avoid' }}>
                            <Box key={userIndex} sx={{ my: 3 }}>
                                <Typography variant="subtitle1" fontWeight="bold" color="primary.dark" sx={{ mb: 1, borderBottom: '1px solid #ce93d8' }}>
                                    {user.name}
                                </Typography>
                                <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 1 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#e1bee7' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Clock-in</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Clock-out</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Duration</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {user.punches.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">No punches found for this employee.</TableCell>
                                                </TableRow>
                                            ) : (
                                                user.punches.map((punch, punchIndex) => (
                                                    <TableRow
                                                        key={punchIndex}
                                                        sx={{
                                                            '&:nth-of-type(odd)': { backgroundColor: '#f8f8f8' },
                                                            '&:hover': { backgroundColor: '#e0e0e0', cursor: 'pointer' }
                                                        }}
                                                    >
                                                        <TableCell>{punch.date}</TableCell>
                                                        <TableCell>{punch.clockIn}</TableCell>
                                                        <TableCell sx={{ color: punch.clockOut.includes('Clocked In') ? 'error.main' : 'inherit', fontWeight: punch.clockOut.includes('Clocked In') ? 'bold' : 'normal' }}>{punch.clockOut}</TableCell>
                                                        <TableCell sx={{ color: punch.duration.includes('Clocked In') ? 'error.main' : 'inherit' }}>{punch.duration}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </div>
                    ))}
                </Paper>
            </Box>
        </Container>
    );
});